import React, { useEffect, useState } from 'react';
import { staffClient } from 'clients/staff';
import Table, { Column } from 'ui/Table';
import Loading from 'ui/Loading';
import Section from 'ui/Section';
import { Switch } from 'ui/shadcn';
import Modal from 'ui/Modal';
import Input from 'ui/Input';
import Button from 'ui/Button';
import { toast } from 'react-toastify';
import { PlusIcon } from '@heroicons/react/24/outline';

interface Press {
    id: number;
    title: string;
    source: string;
    press_date: string;
    summary?: string;
    link_url?: string;
    is_visible: boolean;
}

const emptyForm: Omit<Press, 'id'> = {
    title: '', source: '', press_date: new Date().toISOString().slice(0, 10),
    summary: '', link_url: '', is_visible: true,
};

const PressManager: React.FC = () => {
    const [list, setList] = useState<Press[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState<Press | null>(null);
    const [form, setForm] = useState<Omit<Press, 'id'>>({ ...emptyForm });

    useEffect(() => { fetchList(); }, []);

    const fetchList = async () => {
        try {
            const res = await staffClient.get('/website-press');
            setList(res.data);
        } catch {
            toast.error('보도자료를 불러오지 못했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const openCreate = () => {
        setEditing(null);
        setForm({ ...emptyForm });
        setShowModal(true);
    };

    const openEdit = (row: Press) => {
        setEditing(row);
        setForm({
            title: row.title,
            source: row.source,
            press_date: row.press_date ? row.press_date.slice(0, 10) : '',
            summary: row.summary || '',
            link_url: row.link_url || '',
            is_visible: row.is_visible,
        });
        setShowModal(true);
    };

    const handleSave = async () => {
        try {
            if (editing) {
                await staffClient.patch(`/website-press/${editing.id}`, form);
                toast.success('보도자료가 수정되었습니다.');
            } else {
                await staffClient.post('/website-press', form);
                toast.success('보도자료가 추가되었습니다.');
            }
            setShowModal(false);
            fetchList();
        } catch {
            toast.error('저장에 실패했습니다.');
        }
    };

    const handleDelete = async (row: Press) => {
        if (!window.confirm(`"${row.title}" 보도자료를 삭제하시겠습니까?`)) return;
        try {
            await staffClient.delete(`/website-press/${row.id}`);
            toast.success('삭제되었습니다.');
            fetchList();
        } catch {
            toast.error('삭제에 실패했습니다.');
        }
    };

    const handleToggleVisible = async (row: Press) => {
        try {
            const next = !row.is_visible;
            await staffClient.patch(`/website-press/${row.id}`, { is_visible: next });
            setList(prev => prev.map(r => r.id === row.id ? { ...r, is_visible: next } : r));
            toast.success(next ? '홈페이지에 노출됩니다.' : '홈페이지에서 숨김 처리되었습니다.');
        } catch {
            toast.error('상태 변경 실패');
        }
    };

    const formatDate = (d?: string) => {
        if (!d) return '-';
        const date = new Date(d);
        return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
    };

    const columns: Column<Press>[] = [
        { key: 'press_date', header: '보도일', width: '120px', align: 'center', sortable: true, render: (v) => <span className="tabular-nums text-muted-foreground">{formatDate(v)}</span> },
        { key: 'source', header: '매체', width: '180px', sortable: true, render: (v) => <span className="text-muted-foreground">{v}</span> },
        { key: 'title', header: '제목', sortable: true, render: (v) => <span className="font-medium">{v}</span> },
        {
            key: 'is_visible',
            header: '노출',
            width: '70px',
            align: 'center',
            render: (value, row) => (
                <Switch
                    checked={!!value}
                    onClick={(e) => e.stopPropagation()}
                    onCheckedChange={() => handleToggleVisible(row)}
                    aria-label="노출 여부"
                />
            ),
        },
    ];

    return (
        <div>
            <Section
                className="overflow-hidden"
                title={
                    <>
                        언론사 보도
                        {!loading && (
                            <span className="text-xs font-normal text-muted-foreground tabular-nums">
                                총 <span className="font-medium text-foreground">{list.length}</span>건
                            </span>
                        )}
                    </>
                }
                description="홈페이지 회사소개 > 언론사 보도에 표시될 자료를 관리합니다. 행을 클릭하면 수정할 수 있습니다."
                actions={<Button variant="primary" icon={<PlusIcon />} onClick={openCreate}>보도자료 추가</Button>}
                flush
            >
                {loading ? (
                    <Loading text="보도자료를 불러오는 중..." />
                ) : (
                    <Table
                        columns={columns}
                        data={list}
                        pagination
                        pageSize={15}
                        defaultSortKey="press_date"
                        defaultSortOrder="desc"
                        emptyMessage="등록된 보도자료가 없습니다."
                        onRowClick={openEdit}
                        actionsColumnWidth="90px"
                        actions={(row) => (
                            <Button
                                size="small"
                                variant="danger-outline"
                                onClick={(e) => { e.stopPropagation(); handleDelete(row); }}
                            >
                                삭제
                            </Button>
                        )}
                    />
                )}
            </Section>

            <Modal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                title={editing ? '보도자료 수정' : '보도자료 추가'}
                size="large"
            >
                <div className="grid grid-cols-3 gap-3">
                    <div className="form-group col-span-2">
                        <label>제목 *</label>
                        <Input
                            value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            placeholder="기사 제목"
                        />
                    </div>
                    <div className="form-group">
                        <label>보도일 *</label>
                        <Input
                            type="date"
                            value={form.press_date}
                            onChange={(e) => setForm({ ...form, press_date: e.target.value })}
                        />
                    </div>
                </div>

                <div className="form-group">
                    <label>매체 *</label>
                    <Input
                        value={form.source}
                        onChange={(e) => setForm({ ...form, source: e.target.value })}
                        placeholder="예: 동아일보, 전기신문"
                    />
                </div>

                <div className="form-group">
                    <label>요약/내용</label>
                    <textarea
                        value={form.summary}
                        onChange={(e) => setForm({ ...form, summary: e.target.value })}
                        placeholder="보도 내용 요약"
                        rows={4}
                        className="w-full rounded border border-input bg-background px-3 py-2 text-base text-foreground placeholder:text-neutral-400 focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring resize-none"
                    />
                </div>

                <div className="form-group">
                    <label>원문 링크</label>
                    <Input
                        type="url"
                        value={form.link_url}
                        onChange={(e) => setForm({ ...form, link_url: e.target.value })}
                        placeholder="https://..."
                    />
                </div>

                <div className="modal-footer">
                    <Button variant="primary" onClick={handleSave} disabled={!form.title || !form.source || !form.press_date}>
                        {editing ? '수정' : '추가'}
                    </Button>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>취소</Button>
                </div>
            </Modal>
        </div>
    );
};

export default PressManager;
